import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, contentChild, ElementRef, input, viewChild } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { ChatPanelMessage } from '@coin/chat-bot-util-types';
import { EmployeePictureComponent } from '@coin/shared/core/ui-storybook';
import { animationFrameScheduler, merge, observeOn } from 'rxjs';
import { ChatPanelAssistantMessageDirective } from '../../directives/chat-panel-assistant-message.directive';

/** Chat embedded next to a feature's content. The feature owns the conversation and its agent and projects the input footer. */
@Component({
  selector: 'coin-chat-panel',
  imports: [NgTemplateOutlet, EmployeePictureComponent],
  templateUrl: './chat-panel.component.html',
  styleUrl: './chat-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ChatPanelComponent {
  readonly messages = input.required<ChatPanelMessage[]>();
  readonly isReplying = input(false);
  readonly emptyChatText = input<string>();
  readonly userGid = input.required<string>();

  protected readonly assistantMessage = contentChild(ChatPanelAssistantMessageDirective);

  private readonly messagesContainer = viewChild<ElementRef<HTMLElement>>('messagesContainer');

  constructor() {
    this.scrollToLatestMessage();
  }

  private scrollToLatestMessage(): void {
    merge(toObservable(this.messages), toObservable(this.isReplying))
      // The animation frame deferral ensures the new message is rendered before the scroll height is read
      .pipe(observeOn(animationFrameScheduler), takeUntilDestroyed())
      .subscribe(() => {
        const container = this.messagesContainer()?.nativeElement;
        container?.scrollTo({ top: container.scrollHeight, behavior: 'smooth' });
      });
  }
}
